import { executeLongJob, get, getLocale, post } from '../../js/auth';
import { store } from "../..";
import { I18n } from 'react-redux-i18n';


/**
 * Service for handling incoming stock operations
 * Migrated from incoming-stocks.js jQuery implementation
 */
class IncomingStockService {
    /**
     * Update the draft state of an incoming stock
     * @param {number} stockId - The ID of the incoming stock
     * @param {boolean} draft - The new draft state
     * @returns {Promise} API response promise
     */
    updateDraftState(stockId, draft) {
        // Get the current stock data first
        return this.getIncomingStock(stockId)
            .then(stock => {
                if (!stock) throw new Error('Stock not found');
                // Prepare updated stock object using saveIncomingStock logic
                const processedStockData = {
                    supplier: stock.supplier ? { id: stock.supplier.id } : null,
                    invoice_id: stock.invoice_id || "",
                    delivery_note_id: stock.delivery_note_id || "",
                    date: stock.date,
                    draft: draft,
                    entries: (stock.entries || []).map(entry => {
                        const processedEntry = {
                            count: entry.count,
                            menuItem: entry.menuItem ? {
                                id: entry.menuItem.id,
                                type: entry.menuItem.type || "meal",
                                approvedCost: entry.availableQuantities.find(q => q.quantity === entry.quantity)?.approvedCost || null,
                            } : null,
                            code: entry.code,
                            multiplicator: entry.multiplicator || 1,
                            store: entry.store ? { id: entry.store.id } : null,
                            quantity: entry.quantity,
                            vatCategory: entry.vatCategory ? { id: entry.vatCategory.id } : null,
                            quantityType: entry.quantityType,
                            net_unit_price: entry.net_unit_price,
                            brutto_unit_price: entry.brutto_unit_price,
                            net_price: entry.net_price,
                            brutto_price: entry.brutto_price,
                            expiryDate: entry.expiry_date || entry.expiryDate || null,
                            ignored: entry.ignored || false
                        };
                        if (entry.id) {
                            processedEntry.id = entry.id;
                        }
                        if (processedEntry.quantityType && typeof processedEntry.quantityType === 'string') {
                            const parts = processedEntry.quantityType.split(/[;,]/);
                            if (parts.length === 2) {
                                const quantity = parts[0].trim();
                                const unit = parts[1].trim();
                                processedEntry.quantity = parseFloat(quantity) || 1;
                                processedEntry.quantityType = unit;
                            }
                        }
                        return processedEntry;
                    })
                };
                if (stock.id) {
                    processedStockData.id = stock.id;
                }
                return this.modifyIncomingStock(processedStockData);
            });
    }

    /**
     * Get incoming stocks with optional filters
     * @param {Object} filters - Filter options
     * @returns {Promise} API response promise
     */
    getIncomingStocks(filters = {}) {
        const params = new URLSearchParams();

        // Always add from and to parameters, use defaults if not provided
        const fromDate = filters.from || localStorage.admin_stocklist_start || new Date().getTime();
        const toDate = filters.to || localStorage.admin_stocklist_end || new Date().getTime();

        params.append('from', fromDate);
        params.append('to', toDate);

        // Add other filters to parameters
        if (filters.supplier) params.append('supplier', filters.supplier);
        if (filters.invoice) params.append('invoice', filters.invoice);
        if (filters.deliveryNote) params.append('deliveryNote', filters.deliveryNote);
        if (filters.menuitem) params.append('menuitem', filters.menuitem);
        if (filters.quantity) params.append('quantity', filters.quantity);
        if (filters.store) params.append('store', filters.store);

        const url = `adminService/${localStorage.restaurantSelected}/getIncomingStocks?${params.toString()}`;

        return get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                return response;
            })
            .catch(error => {
                console.error('Error fetching incoming stocks:', error);
                throw error;
            });
    }

    /**
     * Get a single incoming stock by ID
     * @param {number} stockId - Stock ID to fetch
     * @returns {Promise} API response promise
     */
    getIncomingStock(stockId) {
        const url = `adminService/${localStorage.restaurantSelected}/incomingStock/${stockId}`;

        return get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                return response;
            })
            .catch(error => {
                console.error('Error fetching incoming stock:', error);
                throw error;
            });
    }

    /**
     * Get VAT categories from database
     * @returns {Promise} API response promise
     */
    getVATCategories() {
        const reduxState = store.getState();
        if (reduxState.rootReducer?.vatCategories) {
            return Promise.resolve(reduxState.rootReducer.vatCategories);
        }

        if (this._vatCategoriesPromise) {
            return this._vatCategoriesPromise;
        }

        const url = `adminService/${localStorage.restaurantSelected}/getVATCategories`;
        this._vatCategoriesPromise = get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                const vatCategories = response?.map(vat => ({
                    id: `${vat.id};${vat.percent}`,
                    name: `${vat.name} (${vat.percent}%)`,
                    percent: vat.percent,
                    originalId: vat.id
                })) || [];
                if (store) {
                    store.dispatch({ type: "vatCategories", vatCategories });
                }
                this._vatCategoriesPromise = null;
                return vatCategories;
            })
            .catch(error => {
                this._vatCategoriesPromise = null;
                console.error('Error fetching VAT categories:', error);
                return [];
            });
        return this._vatCategoriesPromise;
    }

    /**
     * Get stores from database
     * @returns {Promise} API response promise
     */
    getStores() {
        if (store.getState().rootReducer?.stores) {
            return Promise.resolve(store.getState().rootReducer.stores);
        }

        if (this._storesPromise) {
            return this._storesPromise;
        }
        const url = `adminService/${localStorage.restaurantSelected}/getStores`;
        this._storesPromise = get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                const stores = response || [];
                if (store) {
                    store.dispatch({ type: "stores", stores });
                }
                this._storesPromise = null;
                return stores;
            })
            .catch(error => {
                this._storesPromise = null;
                console.error('Error fetching stores:', error);
                return [];
            });
        return this._storesPromise;
    }

    /**
     * Get quantity types from database
     * @returns {Promise} API response promise
     */
    getQuantityTypes() {
        if (store.getState().rootReducer?.quantityTypes) {
            return Promise.resolve(store.getState().rootReducer.quantityTypes);
        }
        if (this._quantityTypesPromise) {
            return this._quantityTypesPromise;
        }
        const url = `adminService/${localStorage.restaurantSelected}/getQuantities`;
        this._quantityTypesPromise = get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                const quantityTypes = response?.map(qty => ({
                    id: qty.id || qty,
                    name: qty.name || qty,
                    value: qty.value || qty
                })) || [];
                if (store) {
                    store.dispatch({ type: "quantityTypes", quantityTypes });
                }
                this._quantityTypesPromise = null;
                return quantityTypes;
            })
            .catch(error => {
                this._quantityTypesPromise = null;
                console.error('Error fetching quantity types:', error);
                // Return some default quantity types as fallback
                const fallback = [
                    { id: 'kg', name: 'Kilogram', value: 'kg' },
                    { id: 'piece', name: 'Piece', value: 'piece' },
                    { id: 'liter', name: 'Liter', value: 'liter' }
                ];
                if (store) {
                    store.dispatch({ type: "quantityTypes", quantityTypes: fallback });
                }
                return fallback;
            });
        return this._quantityTypesPromise;
    }

    /**
     * Add new incoming stock
     * @param {Object} incomingStock - Stock data to add
     * @returns {Promise} API response promise
     */
    addIncomingStock(incomingStock) {
        const url = `adminService/${localStorage.restaurantSelected}/addIncomingStock?lang=${localStorage.language}`;

        return executeLongJob("", url, false, incomingStock);
    }

    /**
     * Modify existing incoming stock
     * @param {Object} incomingStock - Stock data to modify
     * @returns {Promise} API response promise
     */
    modifyIncomingStock(incomingStock) {
        const url = `adminService/${localStorage.restaurantSelected}/modifyIncomingStock?lang=${localStorage.language}`;

        return executeLongJob("", url, false, incomingStock);
    }

    /**
     * Delete incoming stock by ID
     * @param {number} id - Stock ID to delete
     * @returns {Promise} API response promise
     */
    deleteIncomingStock(id) {
        const url = `adminService/${localStorage.restaurantSelected}/deleteIncomingStock/${id}?lang=${localStorage.language}`;

        return executeLongJob("", url);
    }

    /**
     * Parse number with proper locale handling
     * Migrated from parseNumber function
     * @param {string|number} number - Number to parse
     * @returns {number|null} Parsed number or null
     */
    parseNumber(number) {
        if (number === null || number === undefined || number === '') {
            return null;
        }

        if (typeof number === 'number') {
            return number;
        }

        // Convert string to number, handling localized decimal separators
        const numString = String(number).replace(/,/g, '.');
        const parsed = parseFloat(numString);

        return isNaN(parsed) ? null : parsed;
    }

    /**
     * Format number for display
     * @param {number} number - Number to format
     * @param {number} decimals - Number of decimal places
     * @returns {string} Formatted number string
     */
    formatNumber(number, decimals = 2) {
        if (number === null || number === undefined) {
            return '';
        }

        return Number(number).toFixed(decimals);
    }

    /**
     * Calculate price based on other price fields
     * @param {Object} entry - Entry data with prices
     * @param {string} changedField - The field that was changed
     * @returns {Object} Updated entry with calculated prices
     */
    calculatePrices(entry, changedField) {
        const quantity = this.parseNumber(entry.count) || 0;
        let netUnitPrice = this.parseNumber(entry.net_unit_price) || 0;
        let bruttoUnitPrice = this.parseNumber(entry.brutto_unit_price) || 0;
        let netPrice = this.parseNumber(entry.net_price) || 0;
        let bruttoPrice = this.parseNumber(entry.brutto_price) || 0;

        // Get VAT rate from vat category
        const vatRate = this.getVATRate(entry.vatCategory) / 100;

        switch (changedField) {
            case 'net_unit_price':
                bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                netPrice = netUnitPrice * quantity;
                bruttoPrice = bruttoUnitPrice * quantity;
                break;

            case 'brutto_unit_price':
                netUnitPrice = bruttoUnitPrice / (1 + vatRate);
                netPrice = netUnitPrice * quantity;
                bruttoPrice = bruttoUnitPrice * quantity;
                break;

            case 'net_price':
                netUnitPrice = quantity > 0 ? netPrice / quantity : 0;
                bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                bruttoPrice = bruttoUnitPrice * quantity;
                break;

            case 'brutto_price':
                bruttoUnitPrice = quantity > 0 ? bruttoPrice / quantity : 0;
                netUnitPrice = bruttoUnitPrice / (1 + vatRate);
                netPrice = netUnitPrice * quantity;
                break;

            case 'count':
                // Only recalculate non-editable price fields based on which field is currently active
                const activePriceField = localStorage.input_price || 'net_unit_price';

                switch (activePriceField) {
                    case 'net_unit_price':
                        // Keep net_unit_price fixed, recalculate others
                        bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;

                    case 'brutto_unit_price':
                        // Keep brutto_unit_price fixed, recalculate others
                        netUnitPrice = bruttoUnitPrice / (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;

                    case 'net_price':
                        // Keep net_price fixed, recalculate others
                        netUnitPrice = quantity > 0 ? netPrice / quantity : 0;
                        bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;

                    case 'brutto_price':
                        // Keep brutto_price fixed, recalculate others
                        bruttoUnitPrice = quantity > 0 ? bruttoPrice / quantity : 0;
                        netUnitPrice = bruttoUnitPrice / (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        break;

                    default:
                        // Fallback to original logic if no active field is set
                        netPrice = netUnitPrice * quantity;
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;
                }
                break;

            case 'vatCategory': {
                // Recalculate dependent price fields when VAT changes,
                // preserving the currently active input-price field as source.
                const activePriceField = localStorage.input_price || 'net_unit_price';

                switch (activePriceField) {
                    case 'net_unit_price':
                        // Keep net unit price fixed, derive brutto/unit totals from VAT.
                        bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;

                    case 'brutto_unit_price':
                        // Keep brutto unit price fixed, derive net/unit totals from VAT.
                        netUnitPrice = bruttoUnitPrice / (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;

                    case 'net_price':
                        // Keep net total fixed, derive unit and brutto values from VAT.
                        netUnitPrice = quantity > 0 ? netPrice / quantity : 0;
                        bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;

                    case 'brutto_price':
                        // Keep brutto total fixed, derive unit and net values from VAT.
                        bruttoUnitPrice = quantity > 0 ? bruttoPrice / quantity : 0;
                        netUnitPrice = bruttoUnitPrice / (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        break;

                    default:
                        bruttoUnitPrice = netUnitPrice * (1 + vatRate);
                        netPrice = netUnitPrice * quantity;
                        bruttoPrice = bruttoUnitPrice * quantity;
                        break;
                }
                break;
            }

            default:
                break;
        }

        return {
            ...entry,
            net_unit_price: this.formatNumber(netUnitPrice),
            brutto_unit_price: this.formatNumber(bruttoUnitPrice),
            net_price: this.formatNumber(netPrice),
            brutto_price: this.formatNumber(bruttoPrice)
        };
    }

    /**
     * Extract VAT rate from VAT category
     * @param {Object} vatCategory - VAT category object
     * @returns {number} VAT rate percentage
     */
    getVATRate(vatCategory) {
        if (!vatCategory || !vatCategory.id) {
            return 0;
        }
        if (vatCategory.percent)
            return vatCategory.percent;

        // VAT category ID format: "id;percentage"
        const parts = String(vatCategory.id).split(';');
        return parts.length > 1 ? parseFloat(parts[1]) || 0 : 0;
    }

    /**
     * Validate incoming stock entry
     * @param {Object} entry - Entry to validate
     * @returns {Object} Validation result with isValid and errors
     */
    validateEntry(entry) {
        const errors = [];

        // Check if there's a supplier code but no product selected
        // If supplier code exists but no product is selected, skip all validations
        // This is a normal state during import/processing workflows, not an error
        if (entry.code && entry.code.trim() !== '' && (!entry.menuItem || !entry.menuItem.id)) {
            // Return valid state with no errors - this is normal during import processing
            return {
                isValid: true,
                errors: []
            };
        }

        if (!entry.menuItem || !entry.menuItem.id) {
            errors.push('Product is required');
        }

        if (entry.count !== undefined && parseFloat(entry.count) === 0) {
            errors.push('Quantity must not be 0');
        }

        if (!entry.store || !entry.store.id) {
            errors.push('Store is required');
        }

        if (!entry.vatCategory || !entry.vatCategory.id) {
            errors.push('VAT category is required');
        }

        if (!entry.quantityType) {
            errors.push('Quantity type is required');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Validate incoming stock
     * @param {Object} stock - Stock to validate
     * @returns {Object} Validation result with isValid and errors
     */
    validateStock(stock) {
        const errors = [];

        if (!stock.supplier || !stock.supplier.id) {
            errors.push('Supplier is required');
        }

        if (!stock.date) {
            errors.push('Date is required');
        }

        if (!stock.entries || stock.entries.length === 0) {
            errors.push('At least one entry is required');
        } else {
            // Validate each entry
            stock.entries.forEach((entry, index) => {
                const entryValidation = this.validateEntry(entry);
                if (!entryValidation.isValid) {
                    entryValidation.errors.forEach(error => {
                        errors.push(`Entry ${index + 1}: ${error}`);
                    });
                }
            });
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Prepare stock data for API submission
     * @param {Object} stockData - Raw stock data from form
     * @returns {Object} Formatted stock data for API
     */
    prepareStockForSubmission(stockData) {
        const stock = {
            ...stockData,
            entries: stockData.entries.map(entry => ({
                ...entry,
                count: this.parseNumber(entry.count),
                net_unit_price: this.parseNumber(entry.net_unit_price),
                brutto_unit_price: this.parseNumber(entry.brutto_unit_price),
                net_price: this.parseNumber(entry.net_price),
                brutto_price: this.parseNumber(entry.brutto_price)
            }))
        };

        return stock;
    }

    /**
     * Get suppliers for dropdowns
     * @returns {Promise} API response promise
     */
    getSuppliers() {
        if (store.getState().rootReducer?.suppliers) {
            return Promise.resolve(store.getState().rootReducer.suppliers);
        }
        if (this._suppliersPromise) {
            return this._suppliersPromise;
        }
        const url = `adminService/${localStorage.restaurantSelected}/getSuppliers`;
        this._suppliersPromise = get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                const suppliers = response?.data || response || [];
                if (store) {
                    store.dispatch({ type: "suppliers", suppliers });
                }
                this._suppliersPromise = null;
                return suppliers;
            })
            .catch(error => {
                this._suppliersPromise = null;
                console.error('Error fetching suppliers:', error);
                throw error;
            });
        return this._suppliersPromise;
    }

    /**
     * Get menu items/products for dropdowns with category grouping
     * @returns {Promise} API response promise
     */
    getMenuItems(refresh = false) {
        // Prevent concurrent identical requests
        if (!this._menuItemsPromise) {
            this._menuItemsPromise = null;
        }

        // Check Redux for menuCategories first
        const reduxState = store.getState();
        const menuCategories = refresh ? null : reduxState.rootReducer?.menuCategories;
        if (menuCategories && Array.isArray(menuCategories) && menuCategories.length > 0) {
            const groupedOptions = [];
            this.processMenuCategories(menuCategories, groupedOptions);
            return Promise.resolve(groupedOptions);
        }

        // If a request is already in flight, return the same promise
        if (this._menuItemsPromise) {
            return this._menuItemsPromise;
        }

        // Otherwise, fetch from API and track the promise
        const url = `adminService/${localStorage.restaurantSelected}/getMenuItems?lang=${localStorage.language}&deleted=false&active=false&instance=${localStorage.instance}`;
        this._menuItemsPromise = get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                const groupedOptions = [];
                if (response && response.activeMenuCategories) {
                    this.processMenuCategories(response.activeMenuCategories, groupedOptions);
                }
                store.dispatch({ type: "menuCategories", menuCategories: response.activeMenuCategories });
                this._menuItemsPromise = null; // Clear after completion
                return groupedOptions;
            })
            .catch(error => {
                this._menuItemsPromise = null;
                console.error('Error fetching menu items:', error);
                throw error;
            });
        return this._menuItemsPromise;
    }

    /**
     * Process menu categories to create grouped options for dropdowns
     * @param {Array} categories - Array of categories
     * @param {Array} groupedOptions - Target array for grouped options
     * @param {Array} categoryPath - Current category path for hierarchy
     */
    processMenuCategories(categories, groupedOptions, categoryPath = []) {
        categories.forEach(category => {
            if (category.entityType && category.entityType.indexOf("Category") !== -1) {
                // This is a category - add to current path
                const currentCategoryPath = [...categoryPath, category.name];

                // Create a group
                const categoryGroup = {
                    label: category.name,
                    name: category.name,
                    id: category.id,
                    menuCategoryId: category.menuCategoryId,
                    items: []
                };

                // Process activeMenuItems in this category
                if (category.activeMenuItems && category.activeMenuItems.length > 0) {
                    category.activeMenuItems.forEach(item => {
                        if (item.defaultInventoryItem) {
                            // Build hierarchical category name
                            const hierarchicalCategoryName = currentCategoryPath.join(' → ');

                            categoryGroup.items.push({
                                id: item.id,
                                name: this.formatMenuItemName(item),
                                value: item.id,
                                label: this.formatMenuItemName(item),
                                entityType: item.entityType,
                                isInventoryItem: item.isInventoryItem,
                                manufacturer: item.manufacturer,
                                category: category.name, // Keep immediate parent category
                                categoryName: hierarchicalCategoryName, // Full hierarchical path
                                menuCategoryId: item.menuCategoryId,
                                ...item
                            });
                        }
                    });
                }

                // Only add the group if it has items
                if (categoryGroup.items.length > 0) {
                    groupedOptions.push(categoryGroup);
                }

                // Also recursively process any child categories with updated path
                if (category.activeMenuCategories && category.activeMenuCategories.length > 0) {
                    this.processMenuCategories(category.activeMenuCategories, groupedOptions, currentCategoryPath);
                }
            }
        });
    }

    /**
     * Get flattened menu items for search functionality
     * @returns {Promise} API response promise
     */
    getFlatMenuItems() {
        /* cache it in memory to avoid repeated flattening on every search */
        if (this._flatMenuItems) {
            return Promise.resolve(this._flatMenuItems);
        }
        return this.getMenuItems()
            .then(groupedOptions => {
                // Flatten the grouped structure for search
                const flatItems = [];
                groupedOptions.forEach(group => {
                    if (group.items) {
                        flatItems.push(...group.items);
                    }
                });
                this._flatMenuItems = flatItems; // Cache the flattened items
                return flatItems;
            });
    }

    /**
     * Format menu item name according to original template mapping
     * @param {Object} item - Menu item object
     * @returns {string} Formatted name
     */
    formatMenuItemName(item) {
        // Original mapping: "text":[["manufacturer","name"]]
        const parts = [];
        if (item.manufacturer) {
            parts.push(item.manufacturer);
        }
        if (item.name) {
            parts.push(item.name);
        }
        return parts.join(' ');
    }

    /**
     * Search suppliers by name
     * @param {string} query - Search query
     * @returns {Promise} API response promise
     */
    searchSuppliers(query) {
        const params = new URLSearchParams();
        if (query) params.append('search', query);

        const url = `adminService/${localStorage.restaurantSelected}/searchSuppliers?${params.toString()}`;

        return get(url)
            .then(response => {
                if (response && response.error) {
                    throw new Error(response.error);
                }
                return response?.data || response || [];
            })
            .catch(error => {
                console.error('Error searching suppliers:', error);
                throw error;
            });
    }

    /**
     * Search menu items by name
     * @param {string} query - Search query
     * @returns {Promise} API response promise
     */
    searchMenuItems(query) {
        // Use the flattened menu items for search
        return this.getFlatMenuItems()
            .then(menuItems => {
                if (!query || query.length < 2) {
                    return menuItems;
                }

                // Filter items by name containing the query (case-insensitive)
                // Search in both formatted name and original name/manufacturer
                return menuItems.filter(item => {
                    const searchText = query.toLowerCase();
                    const itemName = (item.name || '').toLowerCase();
                    const itemManufacturer = (item.manufacturer || '').toLowerCase();
                    const formattedName = this.formatMenuItemName(item).toLowerCase();
                    const categoryName = (item.category || '').toLowerCase();

                    return itemName.includes(searchText) ||
                        itemManufacturer.includes(searchText) ||
                        formattedName.includes(searchText) ||
                        categoryName.includes(searchText);
                });
            })
            .catch(error => {
                console.error('Error searching menu items:', error);
                throw error;
            });
    }

    getMenuItem(id) {
        // First check the flattened menu items in memory
        return this.getFlatMenuItems()
            .then(menuItems => {
                const foundItem = menuItems.find(item => item.id === id);
                if (foundItem) {
                    return foundItem;
                }
                // If not found, fallback to API call
                const url = `adminService/${localStorage.restaurantSelected}/getMenuItemsByIds`;
                return post(url, [id])
                    .then(response => {
                        if (response && response.error) {
                            throw new Error(response.error);
                        }
                        const menuItem = response.length ? response[0] : null;
                        if (menuItem) {
                            menuItem.name = I18n.t("admin_local.deleted") + ": " + getLocale(menuItem.name);
                        }
                        return menuItem;
                    })
                    .catch(error => {
                        console.error('Error fetching menu item by ID:', error);
                        throw error;
                    });
            });
    }
}

// Create singleton instance
export const incomingStockService = new IncomingStockService();
export default IncomingStockService;